import { useEffect, useRef, useCallback, useMemo } from 'react';

interface PerformanceMetrics {
  componentName: string;
  renderTime: number;
  timestamp: number;
  props?: Record<string, any>;
}

interface PerformanceConfig {
  enableLogging?: boolean;
  enableReporting?: boolean;
  slowRenderThreshold?: number;
  sampleRate?: number;
}

const DEFAULT_CONFIG: PerformanceConfig = {
  enableLogging: process.env.NODE_ENV === 'development',
  enableReporting: process.env.NODE_ENV === 'production',
  slowRenderThreshold: 16, // 16ms for 60fps
  sampleRate: 0.1, // 10% sampling in production
};

// Performance metrics storage
const performanceMetrics: PerformanceMetrics[] = [];
const MAX_METRICS = 100;

// Hook for measuring component render performance
export const useRenderPerformance = (
  componentName: string,
  props?: Record<string, any>,
  config: PerformanceConfig = {}
) => {
  const mergedConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);
  const renderStartTime = useRef<number>(0);
  const mountTime = useRef<number>(0);

  // Start measuring render time
  const startMeasure = useCallback(() => {
    if (mergedConfig.enableLogging || mergedConfig.enableReporting) {
      renderStartTime.current = performance.now();
    }
  }, [mergedConfig]);

  // End measuring render time
  const endMeasure = useCallback(() => {
    if (renderStartTime.current > 0) {
      const renderTime = performance.now() - renderStartTime.current;
      
      // Sample based on configuration
      if (Math.random() > (mergedConfig.sampleRate || 1)) {
        return;
      }

      const metric: PerformanceMetrics = {
        componentName,
        renderTime,
        timestamp: Date.now(),
        props: mergedConfig.enableLogging ? props : undefined,
      };

      // Store metric
      performanceMetrics.push(metric);
      if (performanceMetrics.length > MAX_METRICS) {
        performanceMetrics.shift();
      }

      // Log slow renders in development
      if (mergedConfig.enableLogging && renderTime > (mergedConfig.slowRenderThreshold || 16)) {
        console.warn(
          `Slow render detected in ${componentName}: ${renderTime.toFixed(2)}ms`,
          { props, renderTime }
        );
      }

      // Report metrics in production
      if (mergedConfig.enableReporting && renderTime > (mergedConfig.slowRenderThreshold || 16)) {
        // In a real app, you would send this to your analytics service
        console.info('Performance metric:', metric);
      }
    }
  }, [componentName, props, mergedConfig]);

  // Measure component mount time
  useEffect(() => {
    mountTime.current = performance.now();
    startMeasure();
    
    return () => {
      endMeasure();
    };
  }, [startMeasure, endMeasure]);

  // Measure re-renders
  useEffect(() => {
    startMeasure();
    return endMeasure;
  });

  return {
    startMeasure,
    endMeasure,
    getMountTime: () => mountTime.current,
  };
};

// Hook for measuring API call performance
export const useApiPerformance = () => {
  const measureApiCall = useCallback(async <T>(
    apiCall: () => Promise<T>,
    operationName: string
  ): Promise<T> => {
    const startTime = performance.now();
    
    try {
      const result = await apiCall();
      const duration = performance.now() - startTime;
      
      // Log slow API calls
      if (duration > 1000) { // 1 second threshold
        console.warn(`Slow API call detected: ${operationName} took ${duration.toFixed(2)}ms`);
      }
      
      // Store metric
      performanceMetrics.push({
        componentName: `API:${operationName}`,
        renderTime: duration,
        timestamp: Date.now(),
      });
      
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      // Log failed API calls
      console.error(`API call failed: ${operationName} after ${duration.toFixed(2)}ms`, error);
      
      throw error;
    }
  }, []);

  return { measureApiCall };
};

// Hook for measuring memory usage
export const useMemoryMonitoring = (componentName: string) => {
  const measureMemory = useCallback(() => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
      };
    }
    return null;
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const memoryInfo = measureMemory();
      if (memoryInfo) {
        // Log memory usage if it's high
        const usagePercent = (memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit) * 100;
        if (usagePercent > 80) {
          console.warn(
            `High memory usage in ${componentName}: ${usagePercent.toFixed(1)}%`,
            memoryInfo
          );
        }
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [componentName, measureMemory]);

  return { measureMemory };
};

// Hook for measuring bundle size impact
export const useBundleAnalysis = () => {
  const measureBundleImpact = useCallback((componentName: string, beforeSize: number) => {
    // This would typically be used with webpack-bundle-analyzer data
    const afterSize = performance.now(); // Placeholder
    const impact = afterSize - beforeSize;
    
    console.info(`Bundle impact for ${componentName}: ${impact}ms`);
    
    return impact;
  }, []);

  return { measureBundleImpact };
};

// Utility functions for performance analysis
export const getPerformanceReport = () => {
  const report = {
    totalMetrics: performanceMetrics.length,
    averageRenderTime: 0,
    slowRenders: 0,
    componentBreakdown: {} as Record<string, { count: number; avgTime: number; maxTime: number }>,
  };

  if (performanceMetrics.length === 0) {
    return report;
  }

  // Calculate averages and breakdowns
  const componentStats: Record<string, number[]> = {};
  
  performanceMetrics.forEach(metric => {
    if (!componentStats[metric.componentName]) {
      componentStats[metric.componentName] = [];
    }
    componentStats[metric.componentName].push(metric.renderTime);
    
    if (metric.renderTime > 16) {
      report.slowRenders++;
    }
  });

  // Calculate component breakdown
  Object.entries(componentStats).forEach(([componentName, times]) => {
    const avgTime = times.reduce((sum, time) => sum + time, 0) / times.length;
    const maxTime = Math.max(...times);
    
    report.componentBreakdown[componentName] = {
      count: times.length,
      avgTime: Math.round(avgTime * 100) / 100,
      maxTime: Math.round(maxTime * 100) / 100,
    };
  });

  report.averageRenderTime = Math.round(
    (performanceMetrics.reduce((sum, metric) => sum + metric.renderTime, 0) / performanceMetrics.length) * 100
  ) / 100;

  return report;
};

export const clearPerformanceMetrics = () => {
  performanceMetrics.length = 0;
};

// Performance optimization recommendations
export const getOptimizationRecommendations = () => {
  const report = getPerformanceReport();
  const recommendations: string[] = [];

  // Check for slow renders
  if (report.slowRenders > report.totalMetrics * 0.1) {
    recommendations.push('Consider using React.memo() for components with frequent re-renders');
  }

  // Check for specific component issues
  Object.entries(report.componentBreakdown).forEach(([componentName, stats]) => {
    if (stats.avgTime > 50) {
      recommendations.push(`${componentName} has slow average render time (${stats.avgTime}ms) - consider optimization`);
    }
    
    if (stats.maxTime > 100) {
      recommendations.push(`${componentName} has very slow max render time (${stats.maxTime}ms) - investigate heavy operations`);
    }
  });

  return recommendations;
};